import {EBirdMyDataSchema} from "ebird-mydata-reader";
import {ECOREGION_ENRICHMENT_ENDPOINT} from "./constants";
import {getApiUrl} from "./client";
import type {LocationEcoregionData} from "../helpers/types";

export type EcoregionEnrichmentLocation = {
    locationId: string,
    location: string,
    latitude: number,
    longitude: number,
    stateProvince?: string,
    county?: string
}

export type EcoregionEnrichmentResponse = {
    version: number,
    summary?: {
        locationCount: number,
        matchedLocationCount: number,
        unmatchedLocationCount: number
    },
    locations: Record<string, LocationEcoregionData>
}

const getCoordinate = (coordinate: string | number | undefined) => {
    const parsedCoordinate = Number(coordinate);
    return Number.isFinite(parsedCoordinate) ? parsedCoordinate : undefined;
}

export const getUniqueEcoregionEnrichmentLocations = (observations: EBirdMyDataSchema[]): EcoregionEnrichmentLocation[] => {
    const locations = new Map<string, EcoregionEnrichmentLocation>();

    for (const observation of observations) {
        const latitude = getCoordinate(observation.latitude);
        const longitude = getCoordinate(observation.longitude);
        if (latitude === undefined || longitude === undefined) continue;

        const locationId = observation.locationId || `${latitude.toFixed(6)},${longitude.toFixed(6)}`;
        if (locations.has(locationId)) continue;

        locations.set(locationId, {
            locationId,
            location: observation.location || '',
            latitude,
            longitude,
            stateProvince: observation.stateProvince || undefined,
            county: observation.county || undefined
        });
    }

    return Array.from(locations.values());
}

export const enrichEcoregionLocations = async (locations: EcoregionEnrichmentLocation[]) => {
    if (!locations.length) return {};

    const response = await fetch(getApiUrl(ECOREGION_ENRICHMENT_ENDPOINT), {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({locations})
    });

    if (!response.ok) {
        throw new Error(`Ecoregion enrichment failed with ${response.status}`);
    }

    const enrichment: EcoregionEnrichmentResponse = await response.json();
    return enrichment.locations ?? {};
}
